import { db } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, serverTimestamp, arrayUnion, increment } from 'firebase/firestore';

interface SessionEvent {
  type: string;
  timestamp: number;
  data: any;
}

interface FunnelEvent {
  type: 'payment_error' | 'validation_error' | 'booking_completed' | 'abandoned' | 'step_completed';
  timestamp?: number;
  data: {
    step?: string;
    error?: {
      message: string;
      details?: any;
    };
    fields?: string[];
  };
}

class SessionRecorder {
  private sessionId: string;
  private events: SessionEvent[] = [];
  private funnelEvents: FunnelEvent[] = [];
  private isRecording: boolean = false;
  private recordingInterval: NodeJS.Timeout | null = null;
  private isReplayMode: boolean = false;
  private firestoreDocId: string | null = null;
  private sessionStartTime: number = 0;
  private lastSaveTime: number = 0;
  private currentStep: string = 'start';

  constructor() {
    this.sessionId = this.generateSessionId();
    this.checkReplayMode();
  }

  private checkReplayMode() {
    const urlParams = new URLSearchParams(window.location.search);
    this.isReplayMode = urlParams.get('replay') === 'true' || urlParams.get('noRecording') === 'true';
    
    if (this.isReplayMode) {
      console.log('🎬 Replay mode detected - session recording disabled');
    }
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  public startRecording() {
    if (this.isRecording || this.isReplayMode) {
      if (this.isReplayMode) {
        console.log('🚫 Recording disabled in replay mode');
      }
      return;
    }
    
    console.log('🎥 Starting session recording with ID:', this.sessionId);
    this.isRecording = true;
    this.events = [];
    this.sessionStartTime = Date.now();
    this.lastSaveTime = Date.now();
    
    // Create initial session document
    this.createInitialSession();
    
    // Record mouse movements (throttled)
    let lastMouseMove = 0;
    const throttledMouseMove = (event: MouseEvent) => {
      const now = Date.now();
      if (now - lastMouseMove > 100) { // Throttle to every 100ms
        this.handleMouseMove(event);
        lastMouseMove = now;
      }
    };
    document.addEventListener('mousemove', throttledMouseMove);
    
    // Record clicks
    document.addEventListener('click', this.handleClick);
    
    // Record scroll events
    document.addEventListener('scroll', this.handleScroll);
    
    // Record form interactions
    document.addEventListener('input', this.handleInput);
    
    // Record page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Record page unload to mark session end
    window.addEventListener('beforeunload', this.handlePageUnload);
    
    // Periodically update the session document
    this.recordingInterval = setInterval(() => {
      this.updateSession();
    }, 5000); // Update every 5 seconds instead of 10
  }

  public stopRecording() {
    if (!this.isRecording || this.isReplayMode) return;
    
    console.log('🛑 Stopping session recording');
    this.isRecording = false;
    
    // Remove event listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('input', this.handleInput);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handlePageUnload);
    
    // Clear interval
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }
    
    // Final session update
    this.finalizeSession();
  }

  private handlePageUnload = () => {
    if (this.isRecording) {
      this.finalizeSession();
    }
  };

  private async createInitialSession() {
    try {
      console.log('📝 Creating initial session document');
      
      const sessionDoc = await addDoc(collection(db, 'sessionRecordings'), {
        sessionId: this.sessionId,
        startTime: serverTimestamp(),
        endTime: null,
        url: window.location.href,
        userAgent: navigator.userAgent,
        events: [],
        funnelEvents: [],
        isActive: true,
        totalEvents: 0,
        duration: 0,
        currentStep: this.currentStep
      });
      
      this.firestoreDocId = sessionDoc.id;
      console.log('✅ Initial session document created:', this.firestoreDocId);
    } catch (error) {
      console.error('❌ Error creating initial session:', error);
    }
  }

  private async updateSession() {
    if (!this.firestoreDocId || (this.events.length === 0 && this.funnelEvents.length === 0)) return;
    
    const eventsToSave = [...this.events];
    const funnelEventsToSave = [...this.funnelEvents];
    this.events = []; // Clear events after copying
    this.funnelEvents = []; // Clear funnel events after copying
    
    try {
      console.log('💾 Updating session with', eventsToSave.length, 'new events and', funnelEventsToSave.length, 'funnel events');
      
      const sessionRef = doc(db, 'sessionRecordings', this.firestoreDocId);
      
      const updateData: any = {
        lastUpdated: serverTimestamp(),
        isActive: true,
        duration: Date.now() - this.sessionStartTime,
        currentStep: this.currentStep
      };

      if (eventsToSave.length > 0) {
        updateData.events = arrayUnion(...eventsToSave);
      }

      if (funnelEventsToSave.length > 0) {
        updateData.funnelEvents = arrayUnion(...funnelEventsToSave);
      }
      
      await updateDoc(sessionRef, updateData);
      
      this.lastSaveTime = Date.now();
      console.log('✅ Session updated successfully');
    } catch (error) {
      console.error('❌ Error updating session:', error);
      // Put events back in the queue if save failed
      this.events = [...eventsToSave, ...this.events];
      this.funnelEvents = [...funnelEventsToSave, ...this.funnelEvents];
    }
  }

  private async finalizeSession() {
    if (!this.isRecording || this.isReplayMode || !this.firestoreDocId) return;
    
    try {
      // Save any remaining events
      if (this.events.length > 0 || this.funnelEvents.length > 0) {
        await this.updateSession();
      }
      
      console.log('🏁 Finalizing session');
      
      const sessionRef = doc(db, 'sessionRecordings', this.firestoreDocId);
      await updateDoc(sessionRef, {
        events: arrayUnion(...this.events),
        funnelEvents: arrayUnion(...this.funnelEvents),
        endTime: serverTimestamp(),
        isActive: false,
        totalEvents: increment(this.events.length),
        duration: Date.now() - this.sessionStartTime
      });
      
      console.log('✅ Session finalized:', this.sessionId);
    } catch (error) {
      console.error('Error finalizing session:', error);
    }
  }

  private handleMouseMove = (event: MouseEvent) => {
    this.events.push({
      type: 'mousemove',
      timestamp: Date.now(),
      data: {
        x: event.clientX,
        y: event.clientY,
        path: this.getElementPath(event.target as Element)
      }
    });
  };

  private handleClick = (event: MouseEvent) => {
    console.log('🖱️ Recording click event');
    const element = event.target as Element;
    this.events.push({
      type: 'click',
      timestamp: Date.now(),
      data: {
        x: event.clientX,
        y: event.clientY,
        path: this.getElementPath(element),
        elementInfo: this.getElementInfo(element)
      }
    });
  };

  private handleScroll = (event: Event) => {
    this.events.push({
      type: 'scroll',
      timestamp: Date.now(),
      data: {
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        path: this.getElementPath(event.target as Element)
      }
    });
  };

  private handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    console.log('⌨️ Recording input event');
    this.events.push({
      type: 'input',
      timestamp: Date.now(),
      data: {
        value: target.value,
        path: this.getElementPath(target),
        elementInfo: this.getElementInfo(target)
      }
    });
  };

  private handleVisibilityChange = () => {
    this.events.push({
      type: 'visibilitychange',
      timestamp: Date.now(),
      data: {
        state: document.visibilityState
      }
    });
  };

  private getElementPath(element: Element): string {
    const path: string[] = [];
    let currentElement: Element | null = element;
    
    while (currentElement && currentElement !== document.body) {
      let selector = currentElement.tagName.toLowerCase();
      if (currentElement.id) {
        selector += `#${currentElement.id}`;
      } else if (currentElement.className) {
        let classNames = [];
        if (typeof currentElement.className === 'string') {
          classNames = currentElement.className.split(' ');
        } else if (currentElement.className && typeof (currentElement.className as any)?.baseVal === 'string') {
          // For SVG elements
          classNames = (currentElement.className as any)?.baseVal.split(' ');
        }
        if (classNames.length > 0) {
          selector += `.${classNames.join('.')}`;
        }
      }
      path.unshift(selector);
      currentElement = currentElement.parentElement;
    }
    
    return path.join(' > ');
  }

  private getElementInfo(element: Element): any {
    const info: any = {
      tagName: element.tagName.toLowerCase(),
      textContent: '',
      attributes: {}
    };

    // Get text content (trimmed and limited)
    if (element.textContent) {
      info.textContent = element.textContent.trim().substring(0, 100);
    }

    // Get important attributes
    const importantAttributes = [
      'id', 'class', 'type', 'name', 'placeholder', 'aria-label', 
      'aria-labelledby', 'title', 'alt', 'value', 'data-testid',
      'role', 'href'
    ];

    importantAttributes.forEach(attr => {
      const value = element.getAttribute(attr);
      if (value) {
        info.attributes[attr] = value;
      }
    });

    // For buttons, try to get the button text from various sources
    if (element.tagName.toLowerCase() === 'button' || element.getAttribute('role') === 'button') {
      // Try to get meaningful button text
      let buttonText = info.textContent;
      
      if (!buttonText && element.getAttribute('aria-label')) {
        buttonText = element.getAttribute('aria-label');
      }
      
      if (!buttonText && element.getAttribute('title')) {
        buttonText = element.getAttribute('title');
      }
      
      // Look for text in child elements
      if (!buttonText) {
        const textElements = element.querySelectorAll('span, div, p');
        for (const textEl of Array.from(textElements)) {
          if (textEl.textContent && textEl.textContent.trim()) {
            buttonText = textEl.textContent.trim();
            break;
          }
        }
      }
      
      if (buttonText) {
        info.buttonText = buttonText.substring(0, 50);
      }
    }

    // For inputs, get label information
    if (element.tagName.toLowerCase() === 'input' || element.tagName.toLowerCase() === 'textarea') {
      // Try to find associated label
      const id = element.getAttribute('id');
      if (id) {
        const label = document.querySelector(`label[for="${id}"]`);
        if (label && label.textContent) {
          info.label = label.textContent.trim();
        }
      }
      
      // Try to find parent label
      const parentLabel = element.closest('label');
      if (parentLabel && parentLabel.textContent) {
        info.label = parentLabel.textContent.trim();
      }
      
      // Get placeholder as fallback
      if (!info.label && element.getAttribute('placeholder')) {
        info.label = element.getAttribute('placeholder');
      }
    }

    // For select elements, get the selected option text
    if (element.tagName.toLowerCase() === 'select') {
      const select = element as HTMLSelectElement;
      if (select.selectedIndex >= 0) {
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption) {
          info.selectedText = selectedOption.textContent?.trim();
        }
      }
    }

    // For links, get the href and link text
    if (element.tagName.toLowerCase() === 'a') {
      info.linkText = info.textContent;
      info.href = element.getAttribute('href');
    }

    return info;
  };

  public logFunnelEvent(type: FunnelEvent['type'], data: FunnelEvent['data']) {
    if (!this.isRecording || this.isReplayMode) return;

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Recording funnel event:', type, data);
    }
    
    const funnelEvent: FunnelEvent = {
      type,
      timestamp: Date.now(),
      data
    };

    this.funnelEvents.push(funnelEvent);
    
    // Immediately save funnel events
    this.updateSession();
  }

  public setCurrentStep(step: string) {
    this.currentStep = step;
  }

  public trackFunnelEvent(event: FunnelEvent) {
    if (!this.isRecording || this.isReplayMode) return;
    
    console.log('📊 Tracking funnel event:', event);
    this.funnelEvents.push({
      ...event,
      timestamp: Date.now()
    });
    
    // Save immediately for important events
    if (event.type === 'payment_error' || event.type === 'validation_error' || event.type === 'booking_completed') {
      this.updateSession();
    }
  }

  private getCurrentStep(): string {
    const lastFunnelEvent = [...this.funnelEvents].reverse().find(event => 
      event.type === 'step_completed' || event.type === 'booking_completed'
    );
    return lastFunnelEvent?.data.step || 'unknown';
  }
}

export const sessionRecorder = new SessionRecorder(); 